import React, { Fragment, useState, useRef, useEffect } from 'react';
import ArchivedBadge from 'components/helpers/ArchivedBadge';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Select from 'react-select-legacy';
import { isEmpty, includes } from 'lodash';
import { ASQLogos, formatDate, columnName } from 'utils/Formatters';
import { isTeachersEnabled } from 'utils/authScopes';
import { hasASQCompleteFeature } from 'utils/features';
import { linkWithParams } from 'routes';
import i18n from '../../utils/i18n';
import { withTranslation } from 'react-i18next';
import PlayerSection from './PlayerSection';
import PlayerSurveyResultsParentEmailSendout from './PlayerSurveyResultsParentEmailSendout';
import PlayerSurveyReminderSendout from './PlayerSurveyReminderSendout';
import { SUPPORTED_LOCALE_CODES } from 'utils/supportedLocales';
import Modal from 'react-responsive-modal';
import { closeIconPath } from '../../containers/modals/ModalRoot';
import { FollowUpParentMessage, LastFollowUpParentMessageSendout } from '../followup/FollowUpParentMessage';
import { FollowUpSurvey } from '../followup/FollowUpSurvey';

const surveyStatusText = s => {
  if (s.state === 'archived')   return { label: 'archived',  color: statusColor('archived') };
  if (s.state === 'completed')  return { label: 'completed', color: statusColor('completed') };
  return { label: s.status, color: statusColor(s.status) };
};

const CardRow = ({ fieldLabel, children }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
    <span style={{ color: '#999', minWidth: 80 }}>{fieldLabel}:</span>
    <span>{children}</span>
  </div>
);

const SurveyStatusCard = ({ label, survey, playerId, location, withStartLink, hasASQComplete, assignedDate, asqType, ageRange, pastSurveys, activePqId, onSelectSurvey, showRescreenPdf, onDownloadPdf, downloadingPqId, playerLocale, locales }) => {
  const { label: statusLabel, color: statusColorVal } = survey ? surveyStatusText(survey) : { label: '—', color: statusColor(null) };
  const canStart = survey && withStartLink && hasASQComplete && !['completed', 'archived'].includes(survey.state);
  const startText = survey && survey.status === 'pending' ? i18n.t('playerSurvey.startNow') : i18n.t('playerSurvey.continue');
  const respondentName = survey && survey.respondentName ? columnName(survey.respondentName) : null;
  return (
    <div style={{ flex: 1, background: '#fff', border: '1px solid #dcdcdc', borderRadius: 4, padding: '8px 16px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #eee' }}>
        {label}{respondentName && <span style={{ fontWeight: 'normal', color: '#666', marginLeft: 6 }}>— {respondentName}</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          {(asqType || ageRange) && (
            <CardRow fieldLabel='ASQ'>
              <b>{asqType}{ageRange ? `: ${ageRange} mo` : ''}</b>
            </CardRow>
          )}
          <CardRow fieldLabel={i18n.t('playerSurvey.status')}>
            {statusLabel === 'archived'
              ? <ArchivedBadge color={statusColorVal} />
              : <span style={{ color: statusColorVal }}><b>{i18n.t(`playerSurvey.${statusLabel}`, { defaultValue: statusLabel })}</b></span>
            }
          </CardRow>
          <CardRow fieldLabel={i18n.t('playerSurvey.assigned')}>{assignedDate || '—'}</CardRow>
          <CardRow fieldLabel={i18n.t('playerSurvey.dueAt')}>{survey && survey.deadlineDate ? formatDate(survey.deadlineDate) : '—'}</CardRow>
        </div>
        {canStart && (
          <div style={{ flexShrink: 0 }}>
            <Link to={{ pathname: linkWithParams('asq', { player_id: playerId, id: survey.id }), state: { from: location } }}
              className='o-sp-button o-sp-button--base o-sp-button--primary'>
              <span className='u-txt--small'><b>{startText}</b></span>
            </Link>
          </div>
        )}
      </div>
      {pastSurveys && onSelectSurvey && (
        <SurveyHistoryPanel
          surveys={pastSurveys}
          activePqId={activePqId}
          onSelect={onSelectSurvey}
          showRescreenPdf={showRescreenPdf}
          onDownloadPdf={onDownloadPdf}
          downloadingPqId={downloadingPqId}
          playerLocale={playerLocale}
          locales={locales}
        />
      )}
    </div>
  );
};


const STATUS_COLORS = {
  completed: '#27ae60',
  pending:   '#e67e22',
  started:   '#2980b9',
  archived:  '#999',
};

const statusColor = (status) => STATUS_COLORS[status] || '#555';

// PDF download button for a survey-history row. Clicking the icon opens a small
// popup listing the available languages; picking one downloads the report in
// that locale.
const RescreenPdfButton = ({ pqId, playerLocale, locales, isDownloading, onDownload }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handlePick = (e, locale) => {
    e.stopPropagation();
    setOpen(false);
    if (!isDownloading) onDownload(pqId, locale);
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type='button'
        title={i18n.t('playerSurvey.downloadPdf', { defaultValue: 'Download PDF' })}
        onClick={(e) => { e.stopPropagation(); if (!isDownloading) setOpen(o => !o); }}
        disabled={isDownloading}
        style={{
          background: 'none', border: 'none', padding: 0, color: '#31ccff',
          cursor: isDownloading ? 'default' : 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, lineHeight: 0, fontSize: 14, verticalAlign: 'middle',
        }}
      >
        {isDownloading
          ? <i className='o-spinner-dual' />
          : <i className='fa fa-file-pdf-o' />}
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', right: 0, top: '100%', zIndex: 20, marginTop: 4,
            background: '#fff', border: '1px solid #dcdcdc', borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: 140, padding: '4px 0', textAlign: 'left',
          }}
        >
          {locales.map((loc) => (
            <button
              key={loc.value}
              type='button'
              onClick={(e) => handlePick(e, loc.value)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', background: 'none',
                border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: '0.95em',
                fontWeight: loc.value === playerLocale ? 'bold' : 'normal', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fbff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              {loc.label}{loc.value === playerLocale ? ' ✅' : ''}
            </button>
          ))}
        </div>
      )}
    </span>
  );
};

const SurveyHistoryPanel = ({ surveys, activePqId, onSelect, showRescreenPdf, onDownloadPdf, downloadingPqId, playerLocale, locales }) => (
  <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 6 }}>
    <div style={{ fontSize: '0.78em', color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Survey history</div>
    <div style={{ display: 'grid', gridTemplateColumns: '12px 1fr 1fr 1fr 28px 80px', gap: '2px 8px', fontSize: '0.75em', color: '#aaa', marginBottom: 2, padding: '0 6px' }}>
      <span /><span>Created</span><span>Status</span><span>Completed</span><span /><span />
    </div>
    {surveys.map((pq, idx) => {
      const isActive = pq.value === activePqId;
      const isMostRecent = idx === 0;
      const statusLabel = pq.state === 'archived' ? 'archived' : (pq.state === 'completed' ? 'completed' : pq.status);
      // PDF download is offered for any parent survey that has parent data
      // (a completed date). Rows without a completed date have nothing to render.
      const canDownloadPdf = showRescreenPdf && !!pq.dateFinished;
      const isDownloading = downloadingPqId === pq.value;
      return (
        <div
          key={pq.value}
          onClick={() => !isActive && onSelect(pq.value)}
          style={{
            display: 'grid', gridTemplateColumns: '12px 1fr 1fr 1fr 28px 80px', gap: '2px 8px',
            alignItems: 'center', padding: '4px 6px', borderRadius: 3, marginBottom: 2,
            background: isActive ? '#e8f7ff' : 'transparent',
            cursor: isActive ? 'default' : 'pointer',
            fontSize: '0.82em',
          }}
          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f5f5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = isActive ? '#e8f7ff' : 'transparent'; }}
        >
          <span style={{ color: '#31ccff', fontWeight: 'bold' }}>{isActive ? '▶' : ''}</span>
          <span style={{ color: '#555' }}>{pq.createdAt || '—'}</span>
          <span style={{ color: statusColor(statusLabel), fontWeight: isActive ? 'bold' : 'normal' }}>{statusLabel}</span>
          <span style={{ color: '#555' }}>{pq.dateFinished || '—'}</span>
          <span style={{ textAlign: 'right' }}>
            {canDownloadPdf && (
              <RescreenPdfButton
                pqId={pq.value}
                playerLocale={playerLocale}
                locales={locales}
                isDownloading={isDownloading}
                onDownload={onDownloadPdf}
              />
            )}
          </span>
          <span style={{ textAlign: 'right' }}>
            {isMostRecent && (
              <span style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#fff', background: '#27ae60', borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap' }}>most recent</span>
            )}
          </span>
        </div>
      );
    })}
  </div>
);


class PlayerSurvey extends React.Component {
  state = {
    followUpParentMessageOpen: false,
    followUpSurveyOpen: false,
    hasUnsavedTranslation: false,
  }

  static propTypes = {
    auth: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    download: PropTypes.object,
    location: PropTypes.object.isRequired,
    player: PropTypes.object,
    survey: PropTypes.object.isRequired,
    questionnaires: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentGroup: PropTypes.object,
    onQuestionInfo: PropTypes.func.isRequired,
    onSurveyChange: PropTypes.func.isRequired,
    onParentPqChange: PropTypes.func.isRequired,
    onTeacherPqChange: PropTypes.func.isRequired,
    onClickNotes: PropTypes.func.isRequired,
    onDownloadASQReport: PropTypes.func,
    asqFollowUpEnabled: PropTypes.bool,
    asqFollowUpStatusManagementEnabled: PropTypes.bool,
    teacherOnlyNoContactEnabled: PropTypes.bool,
    asqFollowUpParentMessagesEnabled: PropTypes.bool,
  };

  onDownloadRescreenPdf = (pqId, locale) => {
    const { onDownloadASQReport, download } = this.props;
    if (download.isDownloading) return;
    onDownloadASQReport(pqId, { locale });
  };

  onClickFollowUpParentMessageOpen = (e) => {
    e.stopPropagation();
    this.setState({ followUpParentMessageOpen: true, hasUnsavedTranslation: false });
  }

  onCloseFollowUpParentMessageOpen = (_e) => {
    if (this.state.hasUnsavedTranslation) {
      if (window.confirm(i18n.t('confirm.unsavedMessage'))) {
        this.setState({ followUpParentMessageOpen: false, hasUnsavedTranslation: false });
      }
    } else {
      this.setState({ followUpParentMessageOpen: false });
    }
  }

  onTranslationStateChange = (hasTranslation) => {
    this.setState({ hasUnsavedTranslation: hasTranslation });
  }

  render() {
    const { auth, isLoading, player, download, survey, questionnaires, currentGroup, selectedTeacherPqId, onQuestionInfo, onSurveyChange, onParentPqChange, onTeacherPqChange, onClickNotes, location, asqFollowUpEnabled, asqFollowUpStatusManagementEnabled, teacherOnlyNoContactEnabled, asqFollowUpParentMessagesEnabled } = this.props;
    if (isEmpty(survey)) return null;
    const isManageAsqFollowUp = auth.isManageAsqFollowUp;
    const showTeacher = isTeachersEnabled(auth);
    const teacherSurvey = showTeacher ? survey.entities.surveys[survey.result.questionnaires.teacher] : null;
    const parentSurvey = survey.entities.surveys[survey.result.questionnaires.parent];
    const withReport = !isEmpty(parentSurvey.asqReportType);
    const group = currentGroup || questionnaires.find(q => q.value === parentSurvey.id) || null;
    const tStatus = (status) => i18n.t(`playerSurvey.${status}`, { defaultValue: status });
    const effectiveTeacherPqId = teacherSurvey ? teacherSurvey.id : (selectedTeacherPqId || null);
    const selectedTeacherGroupData = effectiveTeacherPqId && group && group.teachers
      ? group.teachers.find(tq => tq.value === effectiveTeacherPqId) || null
      : null;
    const showEmailSendout = includes(auth.options, 'asqResultsParentEmailManualSendoutEnabled') && withReport;
    const parentCompleted = parentSurvey && parentSurvey.state === 'completed';
    const teacherCompleted = teacherSurvey && teacherSurvey.state === 'completed';
    const isCurrentParent = !group || !group.parents?.length || parentSurvey.id === group.parents[0].value;
    const withFollowUp = asqFollowUpEnabled && (parentCompleted || teacherCompleted);
    const isPurpleStatus = asqFollowUpStatusManagementEnabled && teacherCompleted && !parentCompleted;
    const locales = SUPPORTED_LOCALE_CODES.map(code => ({ value: code, label: i18n.t(`localeNames.${code}`) }));
    const t = this.props.t;
    const questionnaireOptionRenderer = (opt) => (
      <div>
        <div><b>{opt.ageRange} {t('playerSurvey.month')} - {opt.asqType}</b></div>
        {opt.teacherStatus && (
          <div style={{ fontSize: '0.82em' }}>
            {t('playerSurvey.teacher')}{': '}
            <span style={{ color: statusColor(opt.teacherStatus) }}>{tStatus(opt.teacherStatus)}</span>
            {opt.teacherDateFinished && (
              <span style={{ color: '#888', marginLeft: 4 }}>({opt.teacherDateFinished})</span>
            )}
          </div>
        )}
        <div style={{ fontSize: '0.82em' }}>
          {t('playerSurvey.parent')}{': '}
          <span style={{ color: statusColor(opt.parentStatus) }}>{tStatus(opt.parentStatus)}</span>
          {opt.parentDateFinished && (
            <span style={{ color: '#888', marginLeft: 4 }}>({opt.parentDateFinished})</span>
          )}
        </div>
      </div>
    );
    const rows =
      survey.entities.questionnaires[parentSurvey.questionnaire].sections.map(s =>
        <PlayerSection key={`section-${s}`} section={survey.entities.sections[s]}
          entities={survey.entities}
          auth={auth}
          state={teacherSurvey ? teacherSurvey.state : parentSurvey.state}
          teacherSurvey={teacherSurvey}
          onQuestionInfo={onQuestionInfo} onClickNotes={onClickNotes}
          isLoading={isLoading}/>
      );

    return (
      <Fragment>
        <div className='u-flex u-flex-gap u-margin-bottom--base' style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', maxWidth: '280px' }}>
            <div style={{ fontSize: '0.75em', color: '#888', marginBottom: 3 }}>{this.props.t('playerSurvey.ageRange')}</div>
            <Select
              name='qId'
              className='o-select'
              clearable={false}
              searchable={false}
              value={group ? group.value : parentSurvey.id}
              onChange={onSurveyChange}
              options={questionnaires}
              optionRenderer={questionnaireOptionRenderer}
            />
          </div>
        </div>

        {!isCurrentParent && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: '#31ccff', fontSize: '0.82em' }}>
            <i className='fa fa-info-circle' />
            <span>Action buttons are only enabled for the most recent survey</span>
          </div>
        )}
        <div className='u-flex u-flex-gap u-margin-bottom--base' style={{ alignItems: 'flex-start' }}>
          {asqFollowUpParentMessagesEnabled && isManageAsqFollowUp && (
            <div className='u-flex u-flex-column'>
              <button
                onClick={isCurrentParent && parentCompleted ? this.onClickFollowUpParentMessageOpen : undefined}
                disabled={!isCurrentParent || !parentCompleted}
                className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block c-send-parent-message-button"
              >
                <span className='u-txt--small'>
                  <b>
                    <i className='fa fa-envelope-o u-margin-right--small'></i>
                    {this.props.t('playerSurvey.sendFollowUpParentMessage')}
                  </b>
                </span>
              </button>
              {isCurrentParent && parentCompleted && <LastFollowUpParentMessageSendout survey={parentSurvey} />}
            </div>
          )}
          {showEmailSendout && (
            isCurrentParent && parentCompleted
              ? <PlayerSurveyResultsParentEmailSendout surveyId={parentSurvey.id} />
              : (
                <button disabled className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block c-send-parent-message-button">
                  <span className='u-txt--small'><b><i className='fa fa-envelope-o u-margin-right--small' />{this.props.t('playerSurvey.sendResultsEmail', { defaultValue: 'Send Results Email' })}</b></span>
                </button>
              )
          )}
          {isCurrentParent && parentSurvey.state !== 'completed' && parentSurvey.state !== 'archived'
            ? <PlayerSurveyReminderSendout surveyId={parentSurvey.id} />
            : (
              <div className='u-flex u-flex-column'>
                <button disabled className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block u-float--right c-send-parent-message-button">
                  <span className='u-txt--small'><b><i className='fa fa-envelope-o u-margin-right--small' />ASQ Reminder</b></span>
                </button>
              </div>
            )
          }
          {isManageAsqFollowUp && asqFollowUpEnabled && (
            <div className='u-flex u-flex-column'>
              <button
                onClick={withFollowUp ? () => this.setState({ followUpSurveyOpen: true }) : undefined}
                disabled={!withFollowUp}
                className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block u-float--right c-send-parent-message-button"
              >
                <span className='u-txt--small'>
                  <b>{this.props.t('playerSurvey.viewFollowUp')}</b>
                </span>
              </button>
            </div>
          )}
        </div>

        <div className='u-flex u-flex-gap u-margin-bottom--small' style={{ alignItems: 'stretch' }}>
          {showTeacher && (
            <SurveyStatusCard
              label={this.props.t('playerSurvey.teacher')}
              survey={selectedTeacherGroupData ? {
                id: selectedTeacherGroupData.value,
                state: selectedTeacherGroupData.state,
                status: selectedTeacherGroupData.status,
                respondentName: selectedTeacherGroupData.respondentName,
                deadlineDate: selectedTeacherGroupData.deadlineDate,
              } : teacherSurvey}
              playerId={player.id}
              location={location}
              withStartLink={true}
              hasASQComplete={hasASQCompleteFeature(auth)}
              assignedDate={selectedTeacherGroupData
                ? formatDate(selectedTeacherGroupData.createdAt)
                : (teacherSurvey ? formatDate(teacherSurvey.createdAt) : null)}
              asqType={group ? group.asqType : null}
              ageRange={group ? group.ageRange : null}
              pastSurveys={group?.teachers || []}
              activePqId={effectiveTeacherPqId}
              onSelectSurvey={(id) => onTeacherPqChange({ value: id })}
            />
          )}
          <SurveyStatusCard
            label={this.props.t('playerSurvey.parent')}
            survey={parentSurvey}
            playerId={player.id}
            location={location}
            withStartLink={false}
            hasASQComplete={hasASQCompleteFeature(auth)}
            assignedDate={formatDate(parentSurvey.createdAt)}
            asqType={group ? group.asqType : null}
            ageRange={group ? group.ageRange : null}
            pastSurveys={group?.parents || []}
            activePqId={parentSurvey.id}
            onSelectSurvey={(id) => onParentPqChange({ value: id })}
            showRescreenPdf={withReport}
            onDownloadPdf={this.onDownloadRescreenPdf}
            downloadingPqId={download.isDownloading ? download.id : null}
            playerLocale={player.locale}
            locales={locales}
          />
        </div>
        <div className='o-rtable o-rtable--bordered u-margin-bottom--gutter2x'>
          <div className='o-rtable__row'>
            <div className='o-player-cell o-player-cell__header'>
              <span style={{ fontSize: '0.8em', color: '#888' }}></span>
            </div>
            {showTeacher &&
            <div className='o-player-cell o-player-cell__radio'>
              <span style={{ fontSize: '0.8em', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' }}>{this.props.t('playerSurvey.teacher')}</span>
            </div>}
            <div className='o-player-cell o-player-cell__radio'>
              <span style={{ fontSize: '0.8em', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' }}>{this.props.t('playerSurvey.parent')}</span>
            </div>
            <div className='o-player-cell o-player-cell__align'>
              <span style={{ fontSize: '0.8em', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' }}>{showTeacher ? this.props.t('playerSurvey.align') : this.props.t('playerSurvey.notes')}</span>
            </div>
          </div>
          {rows}
          <div className='o-rtable__row o-rtable__row--bt-bordered'>
            <div className='o-player-cell__logo column'>
              <img alt={parentSurvey.type} src={ASQLogos[parentSurvey.type]}/>
            </div>
          </div>
        </div>
        <Modal
          classNames={{ overlay: 'o-modal__wrap', modal: 'o-modal o-modal__min-height row column u-margin-top--large' }}
          closeIconSvgPath={closeIconPath()}
          closeOnOverlayClick={false}
          open={this.state.followUpParentMessageOpen}
          onClose={this.onCloseFollowUpParentMessageOpen}
          center
        >
          <FollowUpParentMessage
            survey={parentSurvey}
            defaultLocale={this.props.player.locale}
            onSent={() => {
              this.setState({ hasUnsavedTranslation: false }, () => {
                this.onCloseFollowUpParentMessageOpen();
              });
            }}
            onTranslationStateChange={this.onTranslationStateChange}
          />
        </Modal>
        <Modal
          classNames={{
            overlay: 'o-modal__wrap',
            modal: 'o-modal o-modal__min-height row column u-margin-top--large',
            closeButton: 'o-modal__close-with-faq',
          }}
          closeIcon={
            <div className='o-modal__header-actions'>
              <i className='fa fa-times-circle' style={{ fontSize: 26, color: '#31ccff' }}></i>
            </div>
          }
          closeOnOverlayClick={true}
          open={this.state.followUpSurveyOpen}
          onClose={() => this.setState({ followUpSurveyOpen: false })}
          center
        >
          {this.state.followUpSurveyOpen && (
            <FollowUpSurvey
              questionnaireId={parentSurvey.id}
              onSave={() => this.setState({ followUpSurveyOpen: false })}
              auth={auth}
              statusManagementEnabled={asqFollowUpStatusManagementEnabled}
              teacherOnlyNoContactEnabled={teacherOnlyNoContactEnabled}
              isPurpleStatus={isPurpleStatus}
            />
          )}
        </Modal>
      </Fragment>
    );
  }
}

export default withTranslation()(PlayerSurvey);
